import React, { useEffect, useState } from "react";
import _ from "lodash";
import { OcRoleFilter } from "oc/components";
import useOcButtonGroup from "oc/hooks/use-button-group";
import { useTranslation } from "react-i18next";

type UsersSummaryList = {
  total?: number;
  totalRequests?: number;
};

type MonthlyUsersSummaryResponse = {
  __typename: "MonthlyUsersSummaryResponse";
  total?: number;
};

type UsersSummaryListResponse = {
  __typename?: string;
  usersSummaryList?: UsersSummaryList;
};

type UsersSummaryListRow = {
  contractId: string;
  _id: string;
  employeeName: string;
  requestedCost: number;
  // add other fields as needed
};

type UsersSummaryListData = {
  __typename?: string;
  usersSummaryList?: {
    rows?: UsersSummaryListRow[];
    // add other fields as needed
  };
};

type MyTimesheetRoleFilterProps = {
  data:
  | MonthlyUsersSummaryResponse
  | UsersSummaryListResponse
  | UsersSummaryListData
  | undefined;
  product: any;
  role: any;
  refetch: () => void;
  contract: any;
  resetRoleFilters: () => void;
  roleFilters: any;
  setPrevRoleFilters: (filters: any) => void;
  handleAddFilter: (filter: any) => void;
  handleRemoveFilter: (filter: any) => void;
  prevRoleFilters: any;
  buttonGroup: boolean;
  runQuery: () => void;
  setOnlyRequested: (value: boolean) => void;
};

export const MyTimesheetRoleFilter = ({
  data,
  product,
  role,
  refetch,
  resetRoleFilters,
  roleFilters,
  setPrevRoleFilters,
  handleAddFilter,
  handleRemoveFilter,
  prevRoleFilters,
  buttonGroup,
  runQuery,
  setOnlyRequested,
}: MyTimesheetRoleFilterProps) => {
  const { t } = useTranslation();

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({
      id: "MySheetMyTimesheet",
      buttonsDef,
    });

  const [prevSelectedButton, setPrevSelectedButton] = useState(selectedButton);

  const handleClearFilters = () => {
    resetRoleFilters();
    const buttonIndex = buttonsDef.indexOf(prevSelectedButton ?? buttonsDef[0]);
    handleButtonChange(buttonIndex !== -1 ? buttonIndex : 0);
    refetch();
  };

  const handleLoadQuery = () => {
    setPrevRoleFilters(roleFilters);
    setPrevSelectedButton(selectedButton);
    runQuery();
  };

  const buttonDisable =
    _.isEqual(roleFilters, prevRoleFilters) &&
    selectedButton === prevSelectedButton;

  useEffect(() => {
    if (!buttonGroup) return;

    if (selectedButton === "OC_TO_BE_ASSESSED") {
      setOnlyRequested(true);
    } else if (selectedButton === "OC_ALL") {
      setOnlyRequested(false);
    }
  }, [selectedButton, setOnlyRequested, buttonGroup]);

  const isMonthlyUsersSummaryResponse =
    data?.__typename === "MonthlyUsersSummaryResponse";

  let allUsersCount = 0;

  if (isMonthlyUsersSummaryResponse) {
    allUsersCount = (data as MonthlyUsersSummaryResponse)?.total ?? 0;
  } else {
    allUsersCount =
      (data as UsersSummaryListResponse)?.usersSummaryList?.total ?? 0;
  }

  const handleFilter = () => {
    handleLoadQuery();
    runQuery();
  };

  return (
    <>
      <OcRoleFilter
        subtypeProps={{
          roleFilters,
          product,
          role,
          handleAddItem: handleAddFilter,
          handleRemoveItem: handleRemoveFilter,
          queryType: "notAdmin",
          isCheckbox: true,
        }}
        summaryItems={[
          {
            label: "OC_TOTAL_COUNT_OF_EMPLOYEES",
            value: allUsersCount || 0,
          },
          {
            label: "OC_COUNT_OF_REQUESTS",
            value:
              data?.__typename === "UsersSummaryListResponse"
                ? (data as UsersSummaryListResponse)?.usersSummaryList
                  ?.totalRequests || 0
                : 0,
            color: "primary",
          },
        ]}
        buttonBarProps={{
          handlePrimaryButton: handleFilter,
          primaryButtonDisabled: buttonDisable,
          handleRightButton: handleClearFilters,
          paddingTop: "10px",
          rightButtonText: t("OC_RESET"),
          rightButtonId: "cancel-button",
          rightButtonColor: "secondary",
          primaryButtonText: t("OC_FILTERING"),
          primaryButtonId: "filter-button",
          noLine: true,
        }}
        OcButtonGroup={buttonGroup ? OcButtonGroup : undefined}
      />
    </>
  );
};

export default MyTimesheetRoleFilter;
